import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import ClientBackground from "../components/ClientBackground";

export default function TermsAndConditions() {
  const navigate = useNavigate();

  const sections = [
    {
      title: "1. Acceptance of Terms",
      content: [
        "By accessing and using TIFL (The First 1,000 Days), you accept and agree to be bound by the terms and provisions of this agreement.",
        "If you do not agree to these terms, please do not use our services."
      ]
    },
    {
      title: "2. Use of Service",
      content: [
        "You must be at least 18 years old to use this service.",
        "You are responsible for maintaining the confidentiality of your account credentials.",
        "You agree to provide accurate, current, and complete information during registration.",
        "You may not use the service for any illegal or unauthorized purpose."
      ]
    },
    {
      title: "3. Content and Intellectual Property",
      content: [
        "All content provided on TIFL, including text, graphics, logos, and software, is the property of TIFL or its content suppliers.",
        "You may not reproduce, distribute, or create derivative works from our content without explicit permission.",
        "User-generated content remains the property of the user but grants TIFL a license to use, modify, and display such content."
      ]
    },
    {
      title: "4. Medical Disclaimer",
      content: [
        "TIFL provides general information about child development and parenting. This information is not medical advice.",
        "Always consult with qualified healthcare professionals for medical concerns about your child.",
        "TIFL is not liable for any decisions made based on information provided through the service.",
        "In case of emergency, contact your local emergency services immediately."
      ]
    },
    {
      title: "5. Privacy and Data Protection",
      content: [
        "We collect and process personal information as described in our Privacy Policy.",
        "We implement security measures to protect your data but cannot guarantee absolute security.",
        "You have the right to access, correct, or delete your personal information."
      ]
    },
    {
      title: "6. User Conduct",
      content: [
        "You agree not to post harmful, offensive, or inappropriate content.",
        "Harassment, bullying, or threatening behavior is strictly prohibited.",
        "Spam, commercial solicitation, and promotional content are not allowed.",
        "We reserve the right to remove content and suspend accounts that violate these terms."
      ]
    },
    {
      title: "7. Subscription and Payments",
      content: [
        "Some features require a paid subscription.",
        "Subscription fees are billed in advance on a recurring basis.",
        "You may cancel your subscription at any time; cancellation takes effect at the end of the current billing period.",
        "Refunds are provided at our discretion and according to applicable laws."
      ]
    },
    {
      title: "8. Limitation of Liability",
      content: [
        "TIFL is provided 'as is' without warranties of any kind.",
        "We are not liable for any indirect, incidental, or consequential damages.",
        "Our total liability shall not exceed the amount paid by you for the service in the past 12 months."
      ]
    },
    {
      title: "9. Modifications to Terms",
      content: [
        "We reserve the right to modify these terms at any time.",
        "Changes will be effective immediately upon posting.",
        "Continued use of the service after changes constitutes acceptance of new terms."
      ]
    },
    {
      title: "10. Termination",
      content: [
        "We may terminate or suspend your account immediately for violations of these terms.",
        "You may terminate your account at any time from your account settings.",
        "Upon termination, your right to use the service will immediately cease."
      ]
    },
    {
      title: "11. Governing Law",
      content: [
        "These terms are governed by and construed in accordance with applicable laws.",
        "Any disputes shall be resolved through binding arbitration.",
        "You agree to resolve disputes on an individual basis, not as part of a class action."
      ]
    },
    {
      title: "12. Contact Information",
      content: [
        "For questions about these Terms and Conditions, contact us at:",
        "Email: legal@tifl.com",
        "Address: TIFL Legal Department, [Your Address]"
      ]
    }
  ];

  return (
    <div className="relative min-h-screen">
      <ClientBackground />

      <div className="relative z-10">
        <div className="bg-white/30 backdrop-blur-sm px-5 py-4 flex items-center justify-between sticky top-0 z-20">
          <button onClick={() => navigate(-1)} className="p-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <h1 className="text-lg font-semibold text-[#1e3a5f]">Terms & Conditions</h1>
          <div className="w-10" />
        </div>

        <div className="px-5 py-6 max-w-4xl mx-auto pb-20">
          <div className="bg-white rounded-3xl p-8 shadow-lg mb-6">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-3">
              Terms and Conditions
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Last Updated: November 2025
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              Please read these Terms and Conditions carefully before using TIFL. These terms govern your access to and use of our services.
            </p>
          </div>

          <div className="space-y-6">
            {sections.map((section, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-[#1e3a5f] mb-4">
                  {section.title}
                </h3>
                <div className="space-y-3">
                  {section.content.map((paragraph, pIndex) => (
                    <p key={pIndex} className="text-gray-700 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-gradient-to-r from-[#FDB913]/10 to-[#FFA500]/10 border border-[#FDB913]/20 rounded-2xl p-6">
            <p className="text-gray-700 text-sm leading-relaxed">
              By continuing to use TIFL, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}